<script lang="ts" setup>
import { useLocale } from 'vuetify';
import { localeHasDocs } from '~/utils/docs-manifest';

import '@/assets/scss/docs.scss';

const props = defineProps<{
  /** Docs path without locale prefix, e.g. organization-management/shops */
  path: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

type DocLocale = 'en' | 'ja';

const { locale: appLocale, setLocale, t } = useI18n();
const { current: vuetifyLocale } = useLocale();

function toDocLocale(loc: string): DocLocale {
  return loc === 'ja' ? 'ja' : 'en';
}

function defaultDocLocale(): DocLocale {
  const loc = appLocale.value;
  return toDocLocale(localeHasDocs(loc) ? loc : 'en');
}

const selectedLocale = ref<DocLocale>(defaultDocLocale());

watch(
  () => appLocale.value,
  () => {
    selectedLocale.value = defaultDocLocale();
  },
);

const logicalPath = computed(() => `${selectedLocale.value}/${props.path.replace(/^\/+/, '')}`);
const slugParts = computed(() => logicalPath.value.split('/').filter(Boolean));

const { docPage, loading, notFound, rendered } = useDocsPage(logicalPath);
const { getTitle: getNavTitle } = useDocsNavTitles(selectedLocale);

const fullDocHref = computed(() => `/help/${logicalPath.value}`);

async function changeDocLocale(locale: DocLocale) {
  if (locale === selectedLocale.value) {
    return;
  }
  selectedLocale.value = locale;
  setLocale(locale);
  vuetifyLocale.value = locale;
}

const mainScrollRef = ref<HTMLElement | null>(null);

const { activeTocId, finishTocProgrammaticScroll, onTocClick, bindScrollListeners, unbindScrollListeners } =
  useDocsTocScroll(mainScrollRef);

watch(
  () => logicalPath.value,
  () => {
    finishTocProgrammaticScroll();
    nextTick(() => {
      unbindScrollListeners();
      bindScrollListeners();
    });
  },
);

onMounted(() => {
  nextTick(() => bindScrollListeners());
});

onUnmounted(() => {
  unbindScrollListeners();
});
</script>

<template>
  <div class="docs-app docs-app--embedded">
    <header class="docs-app__bar">
      <div class="docs-app__bar-inner">
        <a
          :href="fullDocHref"
          target="_blank"
          class="docs-app__brand"
        >
          <img
            src="/images/logo.png"
            alt="Suge POS"
            width="28"
            height="28"
            class="docs-app__logo"
          />
          <span class="docs-app__title">Suge POS Docs</span>
        </a>

        <div class="docs-app__bar-actions">
          <div
            class="docs-app__locale-switch"
            role="group"
            :aria-label="t('docs.language_aria')"
          >
            <button
              type="button"
              class="docs-app__locale-btn"
              :class="{ 'docs-app__locale-btn--active': selectedLocale === 'en' }"
              @click="changeDocLocale('en')"
            >
              English
            </button>
            <button
              type="button"
              class="docs-app__locale-btn"
              :class="{ 'docs-app__locale-btn--active': selectedLocale === 'ja' }"
              @click="changeDocLocale('ja')"
            >
              日本語
            </button>
          </div>

          <a
            v-if="docPage"
            :href="fullDocHref"
            target="_blank"
            class="docs-app__action-link"
          >
            <v-icon
              icon="mdi-open-in-new"
              size="18"
            />
            <span class="docs-app__action-link-label">{{ t('docs.open_full') }}</span>
          </a>

          <button
            type="button"
            class="docs-app__close-btn"
            :aria-label="t('common.close')"
            @click="emit('close')"
          >
            <v-icon
              icon="mdi-close"
              size="20"
            />
          </button>
        </div>
      </div>
    </header>

    <div
      v-if="loading"
      class="docs-embedded__empty"
    />

    <div
      v-else-if="notFound"
      class="docs-embedded__empty"
    >
      {{ t('docs.not_found') }}
    </div>

    <div
      v-else
      class="docs-page docs-page--embedded"
    >
      <div class="docs-page__columns">
        <main
          ref="mainScrollRef"
          class="docs-page__main"
        >
          <div class="docs-page__main-inner">
            <nav
              class="docs-page__crumbs docs-page__crumbs--desktop"
              :aria-label="t('docs.breadcrumb_aria', {}, { locale: selectedLocale })"
            >
              <span
                v-for="(_, i) in slugParts"
                :key="i"
                class="docs-page__crumb"
              >
                <span
                  v-if="i > 0"
                  class="docs-page__crumb-sep"
                  aria-hidden="true"
                  >/</span
                >
                <a
                  v-if="i < slugParts.length - 1"
                  :href="`/help/${slugParts.slice(0, i + 1).join('/')}`"
                  target="_blank"
                  class="docs-page__crumb-link"
                >
                  {{ getNavTitle(slugParts.slice(0, i + 1).join('/')) }}
                </a>
                <span
                  v-else
                  class="docs-page__crumb-current"
                  >{{ getNavTitle(slugParts.slice(0, i + 1).join('/')) }}</span
                >
              </span>
            </nav>

            <article
              v-if="docPage"
              class="docs-page__article"
            >
              <DocsProse :html="rendered.html" />
            </article>
          </div>
        </main>

        <aside
          v-if="rendered.toc.length"
          class="docs-page__toc"
        >
          <nav
            class="docs-page__toc-sticky"
            :aria-label="t('docs.toc_aria', {}, { locale: selectedLocale })"
          >
            <div class="docs-page__toc-label">{{ t('docs.toc_heading', {}, { locale: selectedLocale }) }}</div>
            <ul class="docs-page__toc-list">
              <li
                v-for="item in rendered.toc"
                :key="item.id"
              >
                <a
                  :href="`#${item.id}`"
                  class="docs-page__toc-link"
                  :class="{
                    'docs-page__toc-link--active': activeTocId === item.id,
                    'docs-page__toc-link--h3': item.level === 3,
                  }"
                  @click.prevent="onTocClick(item.id)"
                >
                  {{ item.text }}
                </a>
              </li>
            </ul>
          </nav>
        </aside>
      </div>
    </div>
  </div>
</template>
